import React, { useState } from 'react';
import { DEFAULT_LIMITS } from '../domain/invitations';
import type { CreatedInvitation, GroupLinkLimits } from '../domain/invitations';
import { offerable, useCollections } from '../hooks/useCollections';
import { useRepositories } from '../hooks/useRepositories';
import { getApiErrorMessage } from '../utils/errors';
import { ActionError, ErrorState } from './PageStates';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, Copy } from 'lucide-react';

interface GroupLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opened from one of my private collections: start with it checked. */
  preselectedCollectionId?: string;
}

/**
 * "Make a group link": a link anyone I send it to can open to connect with
 * me, optionally offering some of my private collections (FR-059). Uses and
 * expiry are the server's defaults — 20 people, 30 days — unless I change
 * them (014 R19).
 */
export default function GroupLinkDialog({
  open,
  onOpenChange,
  preselectedCollectionId,
}: GroupLinkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-none bg-background">
        <DialogHeader>
          <DialogTitle className="text-xl">Make a group link</DialogTitle>
          <DialogDescription>
            Anyone who opens it connects with you — you'll each see the other's open collections.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so the collections load when it's shown. */}
        <GroupLinkForm preselectedCollectionId={preselectedCollectionId} />
      </DialogContent>
    </Dialog>
  );
}

function GroupLinkForm({ preselectedCollectionId }: { preselectedCollectionId?: string }) {
  const collections = useCollections();
  const { invitations } = useRepositories();
  const [checked, setChecked] = useState<Set<string>>(
    new Set(preselectedCollectionId ? [preselectedCollectionId] : []),
  );
  const [label, setLabel] = useState('');
  const [limits, setLimits] = useState<GroupLinkLimits>(DEFAULT_LIMITS);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<CreatedInvitation | null>(null);

  if (created) return <CreatedLink created={created} />;

  if (collections.status === 'loading') {
    return (
      <div role="status" aria-label="Loading your collections" className="space-y-2">
        <Skeleton className="h-12 rounded-2xl" />
        <Skeleton className="h-12 rounded-2xl" />
      </div>
    );
  }
  if (collections.status === 'error') {
    return (
      <ErrorState
        message={getApiErrorMessage(collections.error, 'Could not load.')}
        onRetry={collections.reload}
      />
    );
  }

  const privateOnes = offerable(collections.data);

  const toggle = (id: string, on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      setCreated(
        await invitations.createGroupLink(
          privateOnes.filter((c) => checked.has(c.id)).map((c) => c.id),
          label.trim(),
          limits,
        ),
      );
    } catch (err) {
      setError(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <form onSubmit={create} className="space-y-6">
      <fieldset>
        <legend className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Also offer private collections
        </legend>
        {privateOnes.length === 0 ? (
          <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
            You have no private collections. The link will still connect you.
          </p>
        ) : (
          <ul className="space-y-2">
            {privateOnes.map((c) => {
              const id = `offer-${c.id}`;
              return (
                <li key={c.id} className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3">
                  <Checkbox
                    id={id}
                    checked={checked.has(c.id)}
                    onCheckedChange={(value) => toggle(c.id, value === true)}
                  />
                  <Label htmlFor={id} className="flex-1 cursor-pointer font-medium">
                    {c.name}
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
      </fieldset>

      <div>
        <Label htmlFor="group-link-label">Label (optional)</Label>
        <Input
          id="group-link-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Family chat"
          maxLength={100}
          className="mt-1"
        />
        <p className="mt-1 text-xs text-muted-foreground">Only you see the label.</p>
      </div>

      <LimitsControls limits={limits} onChange={setLimits} />

      {error !== null && <ActionError error={error} />}

      <Button type="submit" className="w-full rounded-full" disabled={creating}>
        {creating ? 'Creating…' : 'Create link'}
      </Button>
    </form>
  );
}

const DEFAULT_USES = 20;
const DEFAULT_DAYS = 30;

/** `YYYY-MM-DD` in local time, `days` from today. */
function dateInDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

function limitsSentence({ maxUses, expiry }: GroupLinkLimits): string {
  const who =
    maxUses.kind === 'unlimited'
      ? 'Anyone with it can use it'
      : `Up to ${maxUses.kind === 'count' ? maxUses.count : DEFAULT_USES} people can use it`;
  const when =
    expiry.kind === 'never'
      ? ', with no expiry.'
      : expiry.kind === 'at'
        ? `, until ${longDate(expiry.at)}.`
        : `, for ${DEFAULT_DAYS} days.`;
  return who + when;
}

/**
 * The link's limits: the default sentence, and on request a number of uses
 * (or "No limit") and an expiry date (or "No expiry"). A control the person
 * touches becomes an explicit value, even if it matches the default.
 */
function LimitsControls({
  limits,
  onChange,
}: {
  limits: GroupLinkLimits;
  onChange: (limits: GroupLinkLimits) => void;
}) {
  const [open, setOpen] = useState(false);
  const [uses, setUses] = useState(String(DEFAULT_USES));
  const [date, setDate] = useState(dateInDays(DEFAULT_DAYS));

  const setUsesText = (text: string) => {
    setUses(text);
    const n = parseInt(text, 10);
    // A blank or bad number keeps the server's default rather than sending nonsense.
    onChange({
      ...limits,
      maxUses: n >= 1 ? { kind: 'count', count: n } : { kind: 'default' },
    });
  };

  const setDateText = (text: string) => {
    setDate(text);
    onChange({
      ...limits,
      expiry: text
        ? { kind: 'at', at: new Date(`${text}T23:59:59`).toISOString() }
        : { kind: 'default' },
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {limitsSentence(limits)}{' '}
        {!open && (
          <button
            type="button"
            className="font-semibold text-primary hover:underline"
            onClick={() => setOpen(true)}
          >
            Change limits
          </button>
        )}
      </p>
      {open && (
        <div className="grid gap-4 rounded-2xl bg-muted p-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="group-link-uses">How many people can use it</Label>
            <Input
              id="group-link-uses"
              type="number"
              min={1}
              value={uses}
              disabled={limits.maxUses.kind === 'unlimited'}
              onChange={(e) => setUsesText(e.target.value)}
              className="mt-1 bg-background"
            />
            <div className="mt-2 flex items-center gap-2">
              <Checkbox
                id="group-link-no-limit"
                checked={limits.maxUses.kind === 'unlimited'}
                onCheckedChange={(on) =>
                  on === true
                    ? onChange({ ...limits, maxUses: { kind: 'unlimited' } })
                    : setUsesText(uses)
                }
              />
              <Label htmlFor="group-link-no-limit" className="cursor-pointer font-normal">
                No limit
              </Label>
            </div>
          </div>
          <div>
            <Label htmlFor="group-link-expiry">Expires on</Label>
            <Input
              id="group-link-expiry"
              type="date"
              min={dateInDays(1)}
              value={date}
              disabled={limits.expiry.kind === 'never'}
              onChange={(e) => setDateText(e.target.value)}
              className="mt-1 bg-background"
            />
            <div className="mt-2 flex items-center gap-2">
              <Checkbox
                id="group-link-no-expiry"
                checked={limits.expiry.kind === 'never'}
                onCheckedChange={(on) =>
                  on === true
                    ? onChange({ ...limits, expiry: { kind: 'never' } })
                    : setDateText(date)
                }
              />
              <Label htmlFor="group-link-no-expiry" className="cursor-pointer font-normal">
                No expiry
              </Label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreatedLink({ created }: { created: CreatedInvitation }) {
  const { invitation, link } = created;
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard can be refused (permissions, insecure origin); the link is
      // still selectable in the field above.
      setCopyFailed(true);
    }
  };

  const until = invitation.expiresAt
    ? new Date(invitation.expiresAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="group-link-url">Invitation link</Label>
        <Input
          id="group-link-url"
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="mt-1 font-mono text-xs"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        {invitation.maxUses !== null ? `Up to ${invitation.maxUses} people` : 'Anyone'}
        {until ? `, until ${until}.` : '.'} Send it wherever your people are.
      </p>
      <Button type="button" className="w-full rounded-full" onClick={copy}>
        {copied ? <Check className="mr-1" /> : <Copy className="mr-1" />}
        {copied ? 'Copied' : 'Copy link'}
      </Button>
      {copyFailed && (
        <p className="text-sm text-muted-foreground">Couldn't copy — select the link above.</p>
      )}
    </div>
  );
}
